/*
 * Dependency-free Keccak-f[1600] used only inside the CPU mining worker.
 * The submit path independently recomputes candidates with viem.
 * Lanes are represented as [low32, high32] pairs to avoid BigInt in the hot loop.
 */

const RATE_BYTES = 136;
const RHO = new Uint8Array([
  0, 1, 62, 28, 27,
  36, 44, 6, 55, 20,
  3, 10, 43, 25, 39,
  41, 45, 15, 21, 8,
  18, 2, 61, 56, 14,
]);
const ROUND_CONSTANTS = new Uint32Array([
  0x00000001, 0x00000000, 0x00008082, 0x00000000,
  0x0000808a, 0x80000000, 0x80008000, 0x80000000,
  0x0000808b, 0x00000000, 0x80000001, 0x00000000,
  0x80008081, 0x80000000, 0x00008009, 0x80000000,
  0x0000008a, 0x00000000, 0x00000088, 0x00000000,
  0x80008009, 0x00000000, 0x8000000a, 0x00000000,
  0x8000808b, 0x00000000, 0x0000008b, 0x80000000,
  0x00008089, 0x80000000, 0x00008003, 0x80000000,
  0x00008002, 0x80000000, 0x00000080, 0x80000000,
  0x0000800a, 0x00000000, 0x8000000a, 0x80000000,
  0x80008081, 0x80000000, 0x00008080, 0x80000000,
  0x80000001, 0x00000000, 0x80008008, 0x80000000,
]);

export function keccak256Bytes(message: Uint8Array): Uint8Array {
  const state = new Uint32Array(50);
  let offset = 0;
  while (offset + RATE_BYTES <= message.length) {
    absorbBlock(state, message.subarray(offset, offset + RATE_BYTES));
    keccakF1600(state);
    offset += RATE_BYTES;
  }
  const finalBlock = new Uint8Array(RATE_BYTES);
  finalBlock.set(message.subarray(offset));
  const delimiterAt = message.length - offset;
  finalBlock[delimiterAt] = finalBlock[delimiterAt]! ^ 0x01;
  finalBlock[RATE_BYTES - 1] = finalBlock[RATE_BYTES - 1]! ^ 0x80;
  absorbBlock(state, finalBlock);
  keccakF1600(state);
  const digest = new Uint8Array(32);
  for (let lane = 0; lane < 4; lane++) {
    writeLittle32(digest, lane * 8, state[lane * 2]!);
    writeLittle32(digest, lane * 8 + 4, state[lane * 2 + 1]!);
  }
  return digest;
}

function absorbBlock(state: Uint32Array, block: Uint8Array): void {
  for (let lane = 0; lane < RATE_BYTES / 8; lane++) {
    const at = lane * 8;
    state[lane * 2] = (state[lane * 2]! ^ readLittle32(block, at)) >>> 0;
    state[lane * 2 + 1] = (state[lane * 2 + 1]! ^ readLittle32(block, at + 4)) >>> 0;
  }
}

function keccakF1600(state: Uint32Array): void {
  const c = new Uint32Array(10);
  const d = new Uint32Array(10);
  const b = new Uint32Array(50);
  for (let round = 0; round < 24; round++) {
    for (let x = 0; x < 5; x++) {
      let low = 0, high = 0;
      for (let y = 0; y < 5; y++) {
        const at = (x + 5 * y) * 2;
        low ^= state[at]!;
        high ^= state[at + 1]!;
      }
      c[x * 2] = low >>> 0;
      c[x * 2 + 1] = high >>> 0;
    }
    for (let x = 0; x < 5; x++) {
      const previous = ((x + 4) % 5) * 2;
      const next = ((x + 1) % 5) * 2;
      const rotated = rotate64(c[next]!, c[next + 1]!, 1);
      d[x * 2] = (c[previous]! ^ rotated[0]) >>> 0;
      d[x * 2 + 1] = (c[previous + 1]! ^ rotated[1]) >>> 0;
    }
    for (let y = 0; y < 5; y++) for (let x = 0; x < 5; x++) {
      const lane = x + 5 * y;
      const at = lane * 2;
      const rotated = rotate64((state[at]! ^ d[x * 2]!) >>> 0,
        (state[at + 1]! ^ d[x * 2 + 1]!) >>> 0, RHO[lane]!);
      const destination = (y + 5 * ((2 * x + 3 * y) % 5)) * 2;
      b[destination] = rotated[0];
      b[destination + 1] = rotated[1];
    }
    for (let y = 0; y < 5; y++) for (let x = 0; x < 5; x++) {
      const at = (x + 5 * y) * 2;
      const one = ((x + 1) % 5 + 5 * y) * 2;
      const two = ((x + 2) % 5 + 5 * y) * 2;
      state[at] = (b[at]! ^ (~b[one]! & b[two]!)) >>> 0;
      state[at + 1] = (b[at + 1]! ^ (~b[one + 1]! & b[two + 1]!)) >>> 0;
    }
    state[0] = (state[0]! ^ ROUND_CONSTANTS[round * 2]!) >>> 0;
    state[1] = (state[1]! ^ ROUND_CONSTANTS[round * 2 + 1]!) >>> 0;
  }
}

function rotate64(low: number, high: number, shift: number): [number, number] {
  if (shift === 0) return [low >>> 0, high >>> 0];
  if (shift === 32) return [high >>> 0, low >>> 0];
  if (shift < 32) return [
    ((low << shift) | (high >>> (32 - shift))) >>> 0,
    ((high << shift) | (low >>> (32 - shift))) >>> 0,
  ];
  const adjusted = shift - 32;
  return [
    ((high << adjusted) | (low >>> (32 - adjusted))) >>> 0,
    ((low << adjusted) | (high >>> (32 - adjusted))) >>> 0,
  ];
}

function readLittle32(bytes: Uint8Array, offset: number): number {
  return (bytes[offset]! | (bytes[offset + 1]! << 8) | (bytes[offset + 2]! << 16) |
    (bytes[offset + 3]! << 24)) >>> 0;
}

function writeLittle32(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset] = value & 0xff;
  bytes[offset + 1] = (value >>> 8) & 0xff;
  bytes[offset + 2] = (value >>> 16) & 0xff;
  bytes[offset + 3] = value >>> 24;
}
